/* Copyright (c) 2022-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Plugin } from '../common/plugin';
import { PluginManager } from '../common/plugin-manager.service';

/**
 * Injection token for providing a Android plugin implementaion.
 * App module **should always** provide this with `multi: true`.
 *
 * `InjectionToken` by default uses "root" for `providedIn`.
 *
 * Example usage:
 * ```
 * providers: [
 *   ...,
 *   {
 *     provide: ANDROID_PLUGINS,
 *     useClass: SomeAndroidPlugin
 *     multi: true                   // <-- REQUIRED
 *   }
 * ]
 * ```
 */
export const ANDROID_PLUGINS = new InjectionToken<Plugin>('AndroidPlugins');

/**
 * Injects all AndroidPlugin which were provided using ANDROID_PLUGINS in app module.
 *
 * Each plugin and their dependencies are instantiated by Angular through `ANDROID_PLUGINS`.
 * Hence all plugins are available to use **only after** the constructor is called.
 */
@Injectable({
    providedIn: 'root'
})
export class AndroidPluginManager extends PluginManager {
    // Since ANDROID_PLUGINS will not be provided by every client,
    // it will be optionally injected.
    constructor(@Optional() @Inject(ANDROID_PLUGINS) plugins: Array<Plugin>) {
        super(plugins || []);
    }
}
